import axios from 'axios'
import mock from './mock/finvise_reports_mock'
import valuation_api from './valuation'
import { filterProdRows, restrictProdOrgIds } from '../utils/prodCompanyFilter'

export { CLIENT, REJECTION_REASONS } from './mock/finvise_reports_mock'

const TZ = 'Europe/Ljubljana'

export const ROLE_USER = 1
export const ROLE_ORGANISATION_ADMIN = 2
export const ROLE_ADMIN = 3
export const ROLE_VALUER = 4
export const ROLE_RETAIL_USER = 5

const bare = axios.create({
	baseURL: process.env.REACT_APP_API_URL,
	withCredentials: true,
	headers: { 'Content-Type': 'application/json' },
})

const ENDPOINTS = {
	orgadmin: '/protected/orgadmin/reports_data',
	admin: '/protected/admin/reports_data',
}

const endpointForRole = role => (role === ROLE_ADMIN ? 'admin' : 'orgadmin')

export const toDateRange = (from, to) => ({
	id: 5,
	name: 'Specific date range',
	start_date: +new Date(from),
	end_date: +new Date(to),
})

let ricsTypeIdPromise = null
export const getRicsTypeId = () => {
	if (!ricsTypeIdPromise) {
		ricsTypeIdPromise = valuation_api
			.getValuationTypes()
			.then(res => {
				const types = (res.data && res.data.types) || []
				const rics = types.find(t => /rics/i.test(t.name || ''))
				return rics ? rics.id : null
			})
			.catch(() => null)
	}
	return ricsTypeIdPromise
}

const buildBody = ({
	page,
	page_size,
	dateRange,
	search,
	sortKey,
	sortDir,
	orgId,
	orgIds,
	reportTypeId,
}) => {
	// orgIds (multi-select, main admin only) takes priority over the single orgId
	const ids = orgIds && orgIds.length ? orgIds : orgId ? [orgId] : null
	return {
		filters: {
			page,
			page_size,
			date_range: dateRange ? [dateRange] : [],
			time_zone: TZ,
			// the backend's where-clause builder matches postcode/address
			// against a `property` filter key, not `search`
			...(search ? { property: search } : {}),
			...(sortKey ? { sort_key: sortKey, sort_dir: sortDir } : {}),
			...(ids
				? {
						organisations: ids,
						...(ids.length === 1 ? { org_id: ids[0] } : {}),
				  }
				: {}),
			...(reportTypeId != null ? { report_types: [reportTypeId] } : {}),
		},
	}
}

const num = v => {
	if (v == null || v === '') return null
	const n = Number(String(v).replace(/[^0-9.\-]/g, ''))
	return isFinite(n) ? n : null
}
const mapType = t => (/rics/i.test(t || '') ? 'RICS' : 'AVM')
const mapStatus = s => {
	const x = (s || '').toLowerCase()
	if (x.includes('cancel')) return 'Cancelled'
	if (x.includes('ready') || x.includes('final') || x.includes('deliver'))
		return 'Submitted'
	return 'In Progress'
}
const ukToISO = (d, t) => {
	if (!d) return null
	const [dd, mm, yy] = String(d).split('/').map(Number)
	if (!dd || !mm) return null
	const year = yy < 100 ? 2000 + yy : yy
	const [H = 0, M = 0, S = 0] = String(t || '')
		.split(':')
		.map(Number)
	const dt = new Date(year, mm - 1, dd, H || 0, M || 0, S || 0)
	return isNaN(+dt) ? null : dt.toISOString()
}
const mapRow = x => {
	const type = mapType(x.report_type)
	return {
		ref: x.reference || (x.pk != null ? '#' + x.pk : ''),
		id: x.pk != null ? x.pk : x.id != null ? x.id : null, // report id for PDF download
		type,
		address: (x.property_addr || '').trim() || null,
		property_addr: x.property_addr,
		postcode: x.postcode,
		region: x.region || null,
		lat: x.lat != null ? Number(x.lat) : null,
		lng: x.lng != null ? Number(x.lng) : null,
		valuer: x.valuer && x.valuer !== 'Unassigned' ? x.valuer : '',
		status: mapStatus(x.current_status),
		valuation: num(
			type === 'RICS'
				? x.final_value
				: x.algo_value != null
				? x.algo_value
				: x.final_value,
		),
		feeEx: num(x.fee_ex != null ? x.fee_ex : x.price_ex_vat),
		feeInc: num(x.fee_inc != null ? x.fee_inc : x.price_inc_vat),
		tier: x.price_tier || null,
		instructed: ukToISO(x.date, x.time),
		submitted: ukToISO(x.final_delivered_date, x.final_delivered_time),
		firstDelivered: ukToISO(x.first_delivered_date, x.first_delivered_time),
		rejectionReason: x.rejection_reason || null,
		cancellationReasonId:
			x.cancellation_reason_id != null ? Number(x.cancellation_reason_id) : null,
		pvqResponseHours:
			x.pvq_response_hours != null ? Number(x.pvq_response_hours) : null,
		organization: x.organization,
		team: x.team,
		teamMember: x.team_member,
		// only populated for role="admin" (see AdminReportRow in the API) —
		// stays undefined/falsy for org admins, who never see the toggle
		archived: x.archived === 'Yes',
	}
}

export async function getReports(opts = {}) {
	const {
		role = ROLE_ORGANISATION_ADMIN,
		orgId = null,
		orgIds = null,
		page = 1,
		page_size = 8,
		dateRange = null,
		search = null,
		sortKey = null,
		sortDir = 1,
		signal = null,
		// surface failures to the caller instead of falling back to mock data
		throwOnError = false,
	} = opts

	// PROD_COMPANY_FILTER: never request data outside the allowed companies
	const restrictedIds = await restrictProdOrgIds(role === ROLE_ADMIN, orgId, orgIds)
	if (restrictedIds && !restrictedIds.length) {
		return {
			report_details: [],
			total: 0,
			aggregates: {
				instructionsAvgTime: null,
				totalFees: 0,
				pvqAvgTime: null,
				casesByRegion: [],
			},
		}
	}

	const roleKey = endpointForRole(role)
	const path = ENDPOINTS[roleKey]
	const reportTypeId = await getRicsTypeId()
	const body = buildBody({
		page,
		page_size,
		dateRange,
		search,
		sortKey,
		sortDir,
		orgId: restrictedIds ? null : orgId,
		orgIds: restrictedIds || orgIds,
		reportTypeId,
	})

	let payload = null
	console.log(`[finvise] → POST ${path} (role_id=${role})`, body)
	try {
		const res = await bare.post(path, body, signal ? { signal } : undefined)
		console.log(`[finvise] ← ${roleKey} (${res.status})`, res.data)
		payload = res.data
	} catch (err) {
		// a caller-triggered abort (a newer filter/page superseded this
		// request) is not a real failure — let it propagate so the caller
		// can skip updating state, instead of quietly resolving to mock data
		if (axios.isCancel(err) || err.code === 'ERR_CANCELED' || throwOnError) {
			throw err
		}
		const st = err.response ? err.response.status : err.message
		console.warn(
			`[finvise] ✗ ${roleKey} reports_data`,
			st,
			err.response && err.response.data,
		)
	}

	if (!payload || !Array.isArray(payload.data)) {
		if (throwOnError) throw new Error('No report rows in the response')
		console.warn('[finvise] no rows from server — using mock data')
		return mock.getReports()
	}

	// belt-and-suspenders: still RICS-only even if the report_types filter
	// above didn't resolve (e.g. the valuation_types lookup failed)
	const rows = filterProdRows(
		payload.data.map(mapRow).filter(r => r.type === 'RICS'),
	)
	const total =
		payload.total_count != null
			? payload.total_count
			: payload.total != null
			? payload.total
			: null
	console.log(
		`[finvise] mapped ${rows.length} rows (total: ${total ?? 'n/a'})`,
		{
			instructions_avg_time: payload.instructions_avg_time,
			total_fees: payload.total_fees,
			pvq_avg_time: payload.pvq_avg_time,
		},
	)
	return {
		report_details: rows,
		total,
		aggregates: {
			instructionsAvgTime: payload.instructions_avg_time,
			totalFees: num(payload.total_fees),
			pvqAvgTime: payload.pvq_avg_time,
			casesByRegion: Array.isArray(payload.cases_by_region)
				? payload.cases_by_region
				: [],
		},
	}
}

export const isRequestCancelled = err => axios.isCancel(err) || err?.code === 'ERR_CANCELED'

export default {
	getReports,
	toDateRange,
	ROLE_ADMIN,
	ROLE_ORGANISATION_ADMIN,
	isRequestCancelled,
}
